import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
// the chat window (with its sockets and emoji picker) is downloaded the first time someone opens it
const SupportChatWidget = lazy(() => import('./SupportChatWidget'));
import './SupportChatLauncher.css';

// Friendly assistant face in the site's maroon / rose palette.
const BotAvatar = () => (
    <svg viewBox="0 0 64 64" width="100%" height="100%" aria-hidden="true">
        <defs>
            <linearGradient id="scl-body" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#ffffff" />
                <stop offset="1" stopColor="color-mix(in srgb, var(--primary) 14%, #fff)" />
            </linearGradient>
            <linearGradient id="scl-face" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="var(--primary)" />
                <stop offset="1" stopColor="var(--primary-dark)" />
            </linearGradient>
        </defs>
        <ellipse cx="32" cy="60" rx="12" ry="2.4" fill="#000" opacity=".12" />
        <path d="M17 58c0-9 6-15 15-15s15 6 15 15z" fill="url(#scl-body)" stroke="color-mix(in srgb, var(--primary) 24%, #fff)" />
        <rect x="9" y="28" width="5" height="10" rx="2.5" fill="color-mix(in srgb, var(--primary) 24%, #fff)" />
        <rect x="50" y="28" width="5" height="10" rx="2.5" fill="color-mix(in srgb, var(--primary) 24%, #fff)" />
        <rect x="12" y="14" width="40" height="32" rx="14" fill="url(#scl-body)" stroke="color-mix(in srgb, var(--primary) 24%, #fff)" />
        <rect x="17" y="20" width="30" height="20" rx="9" fill="url(#scl-face)" />
        <circle cx="26" cy="29" r="2.6" fill="#f6b8c4" />
        <circle cx="38" cy="29" r="2.6" fill="#f6b8c4" />
        <path d="M27 35c2.5 2.2 7.5 2.2 10 0" stroke="#f6b8c4" strokeWidth="2" strokeLinecap="round" fill="none" />
        <path d="M32 14v-5" stroke="var(--primary-light)" strokeWidth="2" strokeLinecap="round" />
        <circle cx="32" cy="8" r="2.5" fill="var(--c-d9a441, #d9a441)" />
    </svg>
);

const POSITION_KEY = 'gettimelam-support-launcher-position';
const EDGE_GAP = 8;

const readPosition = () => {
    try {
        const saved = JSON.parse(localStorage.getItem(POSITION_KEY));
        return Number.isFinite(saved?.left) && Number.isFinite(saved?.top) ? saved : null;
    } catch {
        return null;
    }
};

const clampPosition = (left, top, width = 64, height = 64) => ({
    left: Math.max(EDGE_GAP, Math.min(left, window.innerWidth - width - EDGE_GAP)),
    top: Math.max(EDGE_GAP, Math.min(top, window.innerHeight - height - EDGE_GAP)),
});

const SupportChatLauncher = () => {
    const { user } = useAuth();
    const { pathname } = useLocation();
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState(readPosition);
    const [dragging, setDragging] = useState(false);
    const launcherRef = useRef(null);
    const dragRef = useRef(null);
    const suppressClickRef = useRef(false);

    useEffect(() => {
        const keepInsideWindow = () => {
            if (!position || !launcherRef.current) return;
            const rect = launcherRef.current.getBoundingClientRect();
            const next = clampPosition(position.left, position.top, rect.width, rect.height);
            setPosition(current => current && (current.left !== next.left || current.top !== next.top) ? next : current);
            localStorage.setItem(POSITION_KEY, JSON.stringify(next));
        };
        keepInsideWindow();
        window.addEventListener('resize', keepInsideWindow);
        return () => window.removeEventListener('resize', keepInsideWindow);
    }, [position]);

    const startDrag = (event) => {
        if (event.button !== 0 || !launcherRef.current) return;
        const rect = launcherRef.current.getBoundingClientRect();
        dragRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height,
            moved: false,
        };
        launcherRef.current.setPointerCapture(event.pointerId);
    };

    const moveDrag = (event) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        const dx = event.clientX - drag.startX;
        const dy = event.clientY - drag.startY;
        if (!drag.moved && Math.hypot(dx, dy) < 4) return;
        drag.moved = true;
        setDragging(true);
        setPosition(clampPosition(drag.left + dx, drag.top + dy, drag.width, drag.height));
    };

    const finishDrag = (event) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        if (launcherRef.current?.hasPointerCapture(event.pointerId)) launcherRef.current.releasePointerCapture(event.pointerId);
        if (drag.moved) {
            const finalPosition = clampPosition(
                drag.left + event.clientX - drag.startX,
                drag.top + event.clientY - drag.startY,
                drag.width,
                drag.height,
            );
            setPosition(finalPosition);
            localStorage.setItem(POSITION_KEY, JSON.stringify(finalPosition));
            suppressClickRef.current = true;
        }
        dragRef.current = null;
        setDragging(false);
    };

    // once opened, the window stays mounted (hidden when closed) so the conversation is kept
    const [chatLoaded, setChatLoaded] = useState(false);
    useEffect(() => { if (open) setChatLoaded(true); }, [open]);

    const openChat = (event) => {
        if (suppressClickRef.current) {
            suppressClickRef.current = false;
            event.preventDefault();
            return;
        }
        setOpen(true);
    };

    // Only members and service providers have a support thread; the admin panel has its own inbox.
    if (!user || user.role === 'admin' || pathname.startsWith('/admin')) return null;

    return (
        <>
            {chatLoaded && (
                <Suspense fallback={null}>
                    <SupportChatWidget open={open} onClose={() => setOpen(false)} />
                </Suspense>
            )}
            {!open && (
                <button
                    ref={launcherRef}
                    type="button"
                    className={`support-launcher${dragging ? ' is-dragging' : ''}${position?.left < 230 ? ' tip-to-right' : ''}${position?.top < 65 ? ' tip-below' : ''}`}
                    style={position ? { left: position.left, top: position.top, right: 'auto', bottom: 'auto' } : undefined}
                    onClick={openChat}
                    onPointerDown={startDrag}
                    onPointerMove={moveDrag}
                    onPointerUp={finishDrag}
                    onPointerCancel={finishDrag}
                    aria-label="Chat with the Gettimelam assistant. Drag to move."
                    title="Drag to move · Click to chat"
                >
                    <span className="support-launcher__avatar"><BotAvatar /></span>
                    <span className="support-launcher__tip">Need help? Chat with us</span>
                </button>
            )}
        </>
    );
};

export default SupportChatLauncher;
